import { useEffect, useRef, useState } from "react";
import { createRecord, deleteRecord, fetchAbilities, fetchRecords, updateRecord, loc } from "@/lib/physicsApi";
import PhysicsFrame from "./PhysicsFrame";
import { useT } from "@/i18n/LanguageContext";
import { useSearchParams } from "react-router-dom";
import { fetchCourse } from "@/lib/physicsApi";
import { CompletionPanel, ExerciseReturnBar } from "@/components/academy/LessonNav";
import { flatLessons } from "@/lib/academyTheme";

const DRAFT_KEY = "stoicos.physics.record.draft";
const GROUPS = [
  { title: { en: "The Event", nl: "De Gebeurtenis" }, ids: ["situation", "firstImpression", "preference", "innerProtest", "emotionalResponse"] },
  { title: { en: "The Given and the Chosen", nl: "Het Gegevene en het Gekozene" }, ids: ["given", "agency"] },
  { title: { en: "Causes and the Whole", nl: "Oorzaken en het Geheel" }, ids: ["causalMap", "whole"] },
  { title: { en: "Time and Impermanence", nl: "Tijd en Vergankelijkheid" }, ids: ["timePerspective", "impermanence"] },
  { title: { en: "Role and Virtue", nl: "Rol en Deugd" }, ids: ["role", "virtues"] },
  { title: { en: "Acceptance and Action", nl: "Aanvaarding en Handeling" }, ids: ["acceptanceStatement", "appropriateAction"] },
];

export default function RecordOfReality() {
  const { lang } = useT();
  const L = (v) => loc(v, lang);
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; } });
  const [records, setRecords] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [review, setReview] = useState({});
  const [msg, setMsg] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [returnCtx, setReturnCtx] = useState(null);
  const [searchParams] = useSearchParams();
  const timer = useRef(null);

  useEffect(() => {
    const fromLesson = searchParams.get("fromLesson");
    if (!fromLesson) return;
    fetchCourse().then((course) => {
      const all = flatLessons(course);
      const idx = all.findIndex((l) => l.slug === fromLesson);
      if (idx < 0) return;
      setReturnCtx({ lesson: all[idx], prev: all[idx - 1] || null, next: all[idx + 1] || null, phaseSlug: all[idx].phaseSlug });
    }).catch(() => {});
  }, [searchParams]);

  useEffect(() => {
    fetchAbilities().then((d) => setFields(d.recordFields || [])).catch(() => {});
    fetchRecords().then((d) => setRecords(d.records || [])).catch(() => {});
  }, []);

  const fieldById = (id) => fields.find((f) => f.id === id);
  const setField = (id, value) => {
    const nx = { ...form, [id]: value };
    setForm(nx);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(nx)); } catch { /* full */ }
      setMsg(lang === "nl" ? "Concept automatisch bewaard" : "Draft autosaved");
      setTimeout(() => setMsg(""), 1800);
    }, 600);
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { record } = await createRecord(returnCtx ? { ...form, phaseSlug: returnCtx.phaseSlug, lessonSlug: returnCtx.lesson.slug } : form);
      setRecords((r) => [record, ...r]);
      setJustSaved(true);
      setForm({});
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setMsg(lang === "nl" ? "Ingeschreven in het Record" : "Entered into the Record");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const saveReview = async (rec) => {
    await updateRecord(rec.id, { review: review[rec.id] || "" });
    setRecords((rs) => rs.map((r) => (r.id === rec.id ? { ...r, review: review[rec.id] || "" } : r)));
    setMsg(lang === "nl" ? "Terugblik bewaard" : "Review saved");
    setTimeout(() => setMsg(""), 2000);
  };

  const remove = async (rec) => {
    if (!window.confirm(lang === "nl" ? "Deze invoer definitief verwijderen?" : "Delete this entry permanently?")) return;
    await deleteRecord(rec.id);
    setRecords((rs) => rs.filter((r) => r.id !== rec.id));
  };

  const asTemplate = (rec) => {
    const prefill = {};
    fields.forEach((f) => { if (f.kind !== "text_later" && !["situation", "firstImpression", "emotionalResponse"].includes(f.id)) prefill[f.id] = ""; });
    setForm(prefill);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMsg(lang === "nl" ? "Structuur klaargezet als sjabloon" : "Structure prepared as a template");
    setTimeout(() => setMsg(""), 2500);
  };

  const [saving, setSaving] = useState(false);
  const canSubmit = !saving && ((form.situation || "").trim() || (form.firstImpression || "").trim());

  return (
    <PhysicsFrame title="The Record of Reality" crumb="Record of Reality" testid="physics-record-page" canonicalPath="/record">
      <p className="text-fog/80 font-reading max-w-2xl mb-4 leading-[1.9]">
        {lang === "nl"
          ? "Het gestructureerde journaal van deze discipline. Zestien velden dragen één gebeurtenis van eerste indruk naar passende handeling. Alles hier is privé, alleen voor jouw ogen, en blijft bewaard tussen bezoeken."
          : "The structured journal of this discipline. Sixteen fields carry one event from first impression to appropriate action. Everything here is private, for your eyes only, and persists between visits."}
      </p>
      <ExerciseReturnBar discipline="physics" returnCtx={returnCtx} locTitle={L} />
      <p aria-live="polite" className="text-[11px] text-gold/80 h-4 mb-6" data-testid="physics-record-msg">{msg}</p>
      {justSaved && <CompletionPanel discipline="physics" returnCtx={returnCtx} locTitle={L} />}

      <section className="border border-[#c8a95a]/25 bg-[#14211a] p-6 sm:p-8 space-y-8" aria-label="New record entry">
        {GROUPS.map((g) => (
          <fieldset key={L(g.title)}>
            <legend className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a] mb-4">{L(g.title)}</legend>
            <div className="space-y-4">
              {g.ids.map((id) => {
                const f = fieldById(id);
                if (!f) return null;
                return (
                  <div key={id}>
                    <label htmlFor={`rr-${id}`} className="block text-sm text-bone/90 font-reading">{L(f.label)}</label>
                    <p className="text-[11px] text-fog/55 mb-1.5">{L(f.hint)}</p>
                    <textarea id={`rr-${id}`} rows={2} value={form[id] || ""} data-testid={`physics-record-${id}`}
                      onChange={(e) => setField(id, e.target.value)}
                      className="w-full bg-transparent border border-[#c8a95a]/20 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
        <button data-testid="physics-record-submit" onClick={submit} disabled={!canSubmit}
          className="px-8 py-4 bg-gold/90 text-ink text-xs tracking-[0.22em] uppercase hover:bg-gold disabled:opacity-40 disabled:cursor-not-allowed">
          {lang === "nl" ? "Inschrijven in het Record" : "Enter into the Record"}
        </button>
      </section>

      <section className="mt-16" aria-label="Earlier entries">
        <h2 className="text-xs tracking-[0.3em] uppercase text-[#7fb69a] mb-6">{lang === "nl" ? "Eerdere invoeren" : "Earlier entries"} · {records.length}</h2>
        {records.length === 0 && <p className="text-sm text-fog/60 font-reading">{lang === "nl" ? "Het Record is nog leeg. De eerste gebeurtenis die je erin draagt, opent het." : "The Record is still empty. The first event you carry into it opens it."}</p>}
        <div className="space-y-4">
          {records.map((rec) => {
            const open = openId === rec.id;
            return (
              <article key={rec.id} className="border border-[#c8a95a]/15 bg-[#14211a]">
                <button onClick={() => setOpenId(open ? null : rec.id)} data-testid={`physics-record-entry-${rec.id}`} aria-expanded={open}
                  className="w-full text-left px-5 py-4 flex justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-gold/60">
                  <span className="text-sm text-bone/90 font-reading truncate">{rec.situation || rec.firstImpression}</span>
                  <span className="text-[10px] text-fog/50 whitespace-nowrap">{(rec.createdAt || "").slice(0, 10)}</span>
                </button>
                {open && (
                  <div className="px-5 pb-5 space-y-3">
                    {fields.filter((f) => f.kind !== "text_later" && (rec[f.id] || "").trim()).map((f) => (
                      <p key={f.id} className="text-sm font-reading text-fog leading-relaxed">
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#7fb69a] block">{L(f.label)}</span>{rec[f.id]}
                      </p>
                    ))}
                    <div className="pt-3 border-t border-white/10">
                      <label htmlFor={`review-${rec.id}`} className="block text-[10px] tracking-[0.2em] uppercase text-gold/70 mb-1.5">
                        {lang === "nl" ? "Terugblik — achteraf ingevuld" : "Review — filled in afterwards"}
                      </label>
                      <textarea id={`review-${rec.id}`} rows={2} defaultValue={rec.review || ""} data-testid={`physics-record-review-${rec.id}`}
                        onChange={(e) => setReview((rv) => ({ ...rv, [rec.id]: e.target.value }))}
                        className="w-full bg-transparent border border-[#c8a95a]/20 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                      <div className="flex flex-wrap gap-4 mt-2">
                        <button onClick={() => saveReview(rec)} className="text-[10px] tracking-[0.2em] uppercase text-gold hover:underline" data-testid={`physics-record-save-review-${rec.id}`}>{lang === "nl" ? "Terugblik bewaren" : "Save review"}</button>
                        <button onClick={() => window.print()} className="text-[10px] tracking-[0.2em] uppercase text-[#bfdccb] hover:underline">{lang === "nl" ? "Afdrukken / exporteren" : "Print / export"}</button>
                        <button onClick={() => asTemplate(rec)} className="text-[10px] tracking-[0.2em] uppercase text-[#bfdccb] hover:underline">{lang === "nl" ? "Als sjabloon hergebruiken" : "Reuse as template"}</button>
                        <button onClick={() => remove(rec)} className="text-[10px] tracking-[0.2em] uppercase text-red-300/80 hover:underline" data-testid={`physics-record-delete-${rec.id}`}>{lang === "nl" ? "Verwijderen" : "Delete"}</button>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </PhysicsFrame>
  );
}
